import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "@/context/GameContext";
import { api } from "@/lib/api";
import { t } from "@/lib/translations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Baby, Heart, ShieldCheck, Sparkles, Mail, Lock, User } from "lucide-react";
import mascotWave from "@/assets/mascot/mascot_wave.png";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 mr-2" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);
import onboardWelcome from "@/assets/onboarding/onboard_welcome.png";
import onboardChild from "@/assets/onboarding/onboard_child.png";

export default function OnboardingWizard({ onComplete }) {
  const { language, update, parentId, parentToken } = useGame();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentPassword, setParentPassword] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  
  // Login State
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Forgot Password State
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [hasSentResetCode, setHasSentResetCode] = useState(false);

  // Created parent reference from step 2
  const [createdParent, setCreatedParent] = useState(null);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "316089269187-gadn54190hbnds48u2sv83hc0taqv4vo.apps.googleusercontent.com";

  const handleGoogleCredentialResponse = async (response) => {
    setLoading(true);
    try {
      const parent = await api.googleLogin({ token: response.credential });
      update({
        parentId: parent.id,
        email: parent.email,
        parentToken: parent.token,
        language: parent.language,
        isInvestor: parent.is_investor || false,
        investorSlots: parent.investor_slots || 0,
      });

      // Check if parent already has children profiles registered
      const children = await api.listChildren(parent.id);
      
      toast.success(language === "id" ? "Berhasil masuk dengan Google! 🌟" : "Logged in with Google! 🌟");
      
      if (children && children.length > 0) {
        onComplete();
      } else {
        setIsLoginMode(false);
        setStep(3);
      }
    } catch (err) {
      toast.error(language === "id" ? "Gagal otentikasi Google." : "Google authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!googleClientId) return;

    const initGoogleBtn = () => {
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: handleGoogleCredentialResponse,
            auto_select: false,
          });

          const regContainer = document.getElementById("google-signin-btn-register");
          if (regContainer) {
            regContainer.innerHTML = "";
            window.google.accounts.id.renderButton(regContainer, {
              theme: "outline",
              size: "large",
              type: "standard",
              width: Math.min(regContainer.clientWidth || 320, 360),
            });
          }

          const loginContainer = document.getElementById("google-signin-btn-login");
          if (loginContainer) {
            loginContainer.innerHTML = "";
            window.google.accounts.id.renderButton(loginContainer, {
              theme: "outline",
              size: "large",
              type: "standard",
              width: Math.min(loginContainer.clientWidth || 320, 360),
            });
          }
        } catch (e) {
          console.warn("Failed to render Google Sign-In button:", e);
        }
      }
    };

    initGoogleBtn();
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.google?.accounts?.id || attempts > 20) {
        initGoogleBtn();
        clearInterval(interval);
      }
    }, 250);

    return () => clearInterval(interval);
  }, [googleClientId, step, isLoginMode]);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) return;
    
    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginEmail)) {
      toast.error(language === "id" ? "Format email tidak valid." : "Invalid email format.");
      return;
    }

    setLoading(true);
    try {
      const parent = await api.loginParent({ email: loginEmail, password: loginPassword });
      update({
        parentId: parent.id,
        email: parent.email,
        parentToken: parent.token,
        language: parent.language,
        isInvestor: parent.is_investor || false,
        investorSlots: parent.investor_slots || 0,
      });
      toast.success(language === "id" ? "Berhasil masuk! 🌟" : "Login successful! 🌟");
      onComplete();
    } catch (err) {
      toast.error(language === "id" ? "Email atau password salah." : "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterParent = async () => {
    if (!parentName.trim() || !parentEmail.trim() || !parentPassword.trim()) {
      toast.error(language === "id" ? "Mohon isi semua data orang tua!" : "Please fill in all parent details!");
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(parentEmail)) {
      toast.error(language === "id" ? "Format email tidak valid." : "Invalid email format.");
      return;
    }

    // Password length validation
    if (parentPassword.length < 6) {
      toast.error(language === "id" ? "Password minimal 6 karakter." : "Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const parent = await api.createParent({
        name: parentName.trim(),
        email: parentEmail.trim(),
        password: parentPassword,
        language: language,
      });
      setCreatedParent(parent);
      toast.success(language === "id" ? "Akun orang tua berhasil dibuat!" : "Parent account created!");
      nextStep();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail;
      if (errorMessage === "Email already registered") {
        toast.error(language === "id" 
          ? "Email ini sudah terdaftar. Silakan gunakan tombol masuk." 
          : "This email is already registered. Please login.");
      } else {
        toast.error(language === "id" ? "Gagal mendaftarkan akun." : "Failed to register account.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetCode = async () => {
    if (!forgotEmail) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      toast.error(language === "id" ? "Format email tidak valid." : "Invalid email format.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.forgotPassword({ email: forgotEmail });
      toast.success(language === "id" 
        ? `Kode verifikasi dikirim! Simulasi kode: ${res.code}` 
        : `Verification code sent! Simulated code: ${res.code}`);
      setHasSentResetCode(true);
    } catch (err) {
      console.error(err);
      toast.error(language === "id" ? "Email tidak terdaftar." : "Email not registered.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!forgotEmail || !forgotCode || !forgotNewPassword) return;

    if (forgotNewPassword.length < 6) {
      toast.error(language === "id" ? "Password minimal 6 karakter." : "Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword({
        email: forgotEmail,
        code: forgotCode.trim(),
        new_password: forgotNewPassword,
      });
      toast.success(language === "id" ? "Password berhasil diubah!" : "Password reset successful!");
      setIsForgotMode(false);
      setHasSentResetCode(false);
      setForgotCode("");
      setForgotNewPassword("");
    } catch (err) {
      console.error(err);
      toast.error(language === "id" ? "Kode verifikasi salah." : "Invalid verification code.");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep((s) => s + 1);

  const handleFinish = async () => {
    if (!childName || !childAge) {
      toast.error(language === "id" ? "Mohon isi semua data!" : "Please fill all fields!");
      return;
    }
    
    const activeParentId = createdParent?.id || parentId;
    if (!activeParentId) {
      toast.error(language === "id" ? "Terjadi kesalahan: data orang tua tidak ditemukan." : "Error: parent data not found.");
      return;
    }

    setLoading(true);
    try {
      // 1. Create Child
      const child = await api.createChild({
        parent_id: activeParentId,
        name: childName,
        age: parseInt(childAge),
        avatar_color: "#38BDF8", // default color
      });

      // 2. Update Global State
      update({
        parentId: activeParentId,
        email: createdParent?.email || loginEmail,
        parentToken: createdParent?.token || parentToken,
        activeChildId: child.id,
        language: createdParent?.language || language,
      });
      
      toast.success(language === "id" ? "Profil berhasil dibuat! 🌟" : "Profile created! 🌟");
      onComplete();
      
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail;
      if (msg === "Child profile already exists" || msg === "Profil anak dengan nama dan usia ini sudah terdaftar.") {
        toast.error(language === "id" 
          ? "Profil anak dengan nama dan usia tersebut sudah terdaftar." 
          : "A child profile with this name and age already exists.");
      } else {
        toast.error(language === "id" ? "Gagal menyimpan data anak." : "Failed to save child data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      id: 1,
      content: (
        <div className="flex flex-col items-center text-center space-y-6">
          <div className="flex gap-4 justify-center">
            <img src={onboardWelcome} alt="Welcome" className="h-40 object-contain" />
            <img src={mascotWave} alt="Mascot" className="h-32 object-contain" />
          </div>
          <div>
            <h2 className="text-3xl font-display font-bold text-slate-800 mb-2">
              {language === "id" ? "Selamat Datang di" : "Welcome to"}<br/>
              <span className="text-sky-500">Petualangan</span> <span className="text-rose-400">Bayi</span>
            </h2>
            <p className="text-slate-600 text-lg">
              {language === "id" 
                ? "Aplikasi edukasi interaktif yang dirancang khusus berdasarkan milestone perkembangan anak usia 1-5 tahun." 
                : "Interactive educational app specifically designed based on developmental milestones for ages 1-5."}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mt-4">
            <div className="bg-sky-50 p-4 rounded-2xl flex flex-col items-center">
              <Sparkles className="w-8 h-8 text-sky-500 mb-2" />
              <span className="font-bold text-sm text-slate-700">{language === "id" ? "Interaktif" : "Interactive"}</span>
            </div>
            <div className="bg-rose-50 p-4 rounded-2xl flex flex-col items-center">
              <Heart className="w-8 h-8 text-rose-500 mb-2" />
              <span className="font-bold text-sm text-slate-700">{language === "id" ? "Edukatif" : "Educational"}</span>
            </div>
            <div className="bg-amber-50 p-4 rounded-2xl flex flex-col items-center">
              <ShieldCheck className="w-8 h-8 text-amber-500 mb-2" />
              <span className="font-bold text-sm text-slate-700">{language === "id" ? "Aman" : "Safe"}</span>
            </div>
          </div>
          <div className="w-full space-y-3">
            <Button onClick={nextStep} className="w-full h-14 rounded-2xl text-xl font-bold font-display shadow-[0_4px_0_rgba(0,0,0,0.15)] bg-sky-500 hover:bg-sky-400">
              {language === "id" ? "Mulai Petualangan! 🚀" : "Start Adventure! 🚀"}
            </Button>
            <button onClick={() => setIsLoginMode(true)} className="w-full text-slate-500 font-bold hover:text-sky-600 transition-colors">
              {language === "id" ? "Sudah punya akun? Masuk di sini" : "Already have an account? Login"}
            </button>
          </div>
        </div>
      ),
    },
    {
      id: 2,
      content: (
        <div className="flex flex-col space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-display font-bold text-slate-800 mb-2">
              {language === "id" ? "Halo Ayah / Bunda 👋" : "Hello Parents 👋"}
            </h2>
            <p className="text-slate-600">
              {language === "id" ? "Boleh kami tahu nama Anda?" : "What should we call you?"}
            </p>
          </div>
          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-slate-400" />
              </div>
              <Input 
                placeholder={language === "id" ? "Nama Lengkap" : "Full Name"}
                className="h-14 pl-12 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-slate-400" />
              </div>
              <Input 
                type="email"
                placeholder={language === "id" ? "Alamat Email" : "Email Address"}
                className="h-14 pl-12 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-slate-400" />
              </div>
              <Input 
                type="password"
                placeholder={language === "id" ? "Buat Password" : "Create Password"}
                className="h-14 pl-12 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                value={parentPassword}
                onChange={(e) => setParentPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && parentName && parentEmail && parentPassword && consentChecked && nextStep()}
              />
            </div>
            <div className="flex items-start space-x-2 pt-2 text-left">
              <input 
                type="checkbox" 
                id="parental-consent" 
                className="mt-1 w-4 h-4 rounded text-sky-500 border-slate-300 focus:ring-sky-500 cursor-pointer"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <label htmlFor="parental-consent" className="text-xs text-slate-500 leading-tight cursor-pointer select-none">
                {language === "id" 
                  ? "Saya menyatakan bahwa saya adalah orang tua/wali sah dan menyetujui pemrosesan data tumbuh kembang anak sesuai UU PDP No. 27/2022."
                  : "I declare that I am the parent/legal guardian and consent to the processing of child development data under UU PDP No. 27/2022."}
              </label>
            </div>
          </div>
          
          <div className="pt-2">
            <Button 
              onClick={handleRegisterParent} 
              disabled={!parentName || !parentEmail || !parentPassword || !consentChecked || loading}
              className="w-full h-14 rounded-2xl text-xl font-bold font-display shadow-[0_4px_0_rgba(0,0,0,0.15)] bg-emerald-500 hover:bg-emerald-400"
            >
              {loading ? "..." : (language === "id" ? "Daftar dengan Email" : "Sign Up with Email")}
            </Button>
          </div>

          <div className="relative mt-2 mb-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#FDFBF7] text-slate-400 font-medium">
                {language === "id" ? "ATAU" : "OR"}
              </span>
            </div>
          </div>

          <div className="flex justify-center w-full min-h-[56px] border-2 border-slate-200 rounded-2xl bg-white overflow-hidden hover:bg-slate-50 transition-colors cursor-pointer relative">
            <div 
              id="google-signin-btn-register" 
              className="w-full h-full flex justify-center items-center py-2"
              onClick={() => {
                if (!window.google?.accounts?.id) {
                  toast.error(
                    language === "id"
                      ? "Modul otentikasi Google belum siap atau diblokir. Silakan daftar dengan Email."
                      : "Google auth module not ready or blocked. Please register with Email."
                  );
                }
              }}
            ></div>
          </div>

          <div className="mt-2 text-center">
            <button 
              onClick={() => setIsLoginMode(true)} 
              className="text-slate-500 font-bold hover:text-sky-600 transition-colors text-sm"
            >
              {language === "id" ? "Sudah punya akun? Masuk di sini" : "Already have an account? Login"}
            </button>
          </div>
        </div>
      ),
    },
    {
      id: 3,
      content: (
        <div className="flex flex-col space-y-6">
          <div className="text-center">
            <img src={onboardChild} alt="Child playing" className="h-32 mx-auto object-contain mb-2" />
            <h2 className="text-3xl font-display font-bold text-slate-800 mb-2">
              {language === "id" ? "Profil Jagoan Kecil" : "Little Hero's Profile"}
            </h2>
            <p className="text-slate-600">
              {language === "id" ? "Siapa nama si kecil dan berapa usianya?" : "What's the child's name and age?"}
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="font-bold text-slate-700">{language === "id" ? "Nama Panggilan" : "Nickname"}</label>
              <Input 
                placeholder={language === "id" ? "Contoh: Budi" : "Example: Leo"}
                className="h-14 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="font-bold text-slate-700">{language === "id" ? "Usia (Tahun)" : "Age (Years)"}</label>
              <Select value={childAge} onValueChange={setChildAge}>
                <SelectTrigger className="h-14 text-lg rounded-2xl bg-white border-2 border-slate-200">
                  <SelectValue placeholder={language === "id" ? "Pilih Usia" : "Select Age"} />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((age) => (
                    <SelectItem key={age} value={age.toString()} className="text-lg">
                      {age} {language === "id" ? "Tahun" : "Years Old"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button 
            onClick={handleFinish} 
            disabled={!childName || !childAge || loading}
            className="w-full h-14 rounded-2xl text-xl font-bold font-display shadow-[0_4px_0_rgba(0,0,0,0.15)] bg-rose-500 hover:bg-rose-400"
          >
            {loading ? "Menyimpan..." : (language === "id" ? "Selesai & Mulai Main! 🎉" : "Done & Play! 🎉")}
          </Button>
        </div>
      ),
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="bg-[#FDFBF7] w-full max-w-lg rounded-[2rem] p-6 md:p-8 shadow-2xl relative overflow-hidden"
      >
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-2 bg-slate-100">
          <motion.div 
            className="h-full bg-sky-400"
            initial={{ width: "33%" }}
            animate={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        <div className="mt-4">
          <AnimatePresence mode="wait">
            {isForgotMode ? (
              <motion.div
                key="forgot"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex flex-col space-y-6"
              >
                <div className="text-center">
                  <h2 className="text-3xl font-display font-bold text-slate-800 mb-2">
                    {language === "id" ? "Lupa Password" : "Forgot Password"}
                  </h2>
                  <p className="text-slate-600 text-sm">
                    {language === "id" 
                      ? "Masukkan email terdaftar untuk menyetel ulang password Anda." 
                      : "Enter your registered email to reset your password."}
                  </p>
                </div>
                
                {!hasSentResetCode ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-slate-400" />
                      </div>
                      <Input 
                        type="email"
                        placeholder={language === "id" ? "Alamat Email" : "Email Address"}
                        className="h-14 pl-12 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                      />
                    </div>
                    
                    <Button 
                      onClick={handleSendResetCode} 
                      disabled={!forgotEmail || loading}
                      className="w-full h-14 rounded-2xl text-xl font-bold font-display shadow-[0_4px_0_rgba(0,0,0,0.15)] bg-sky-500 hover:bg-sky-400"
                    >
                      {loading ? "..." : (language === "id" ? "Kirim Kode Reset" : "Send Reset Code")}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-amber-50 rounded-xl text-amber-700 text-xs font-semibold text-center leading-normal">
                      {language === "id" 
                        ? "Simulasi: Gunakan kode reset yang muncul di notifikasi sukses." 
                        : "Simulation: Use the reset code shown in the success notification."}
                    </div>
                    <div className="relative">
                      <Input 
                        placeholder={language === "id" ? "Masukkan 6-Digit Kode" : "Enter 6-Digit Code"}
                        className="h-14 text-center text-lg font-bold rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                        value={forgotCode}
                        onChange={(e) => setForgotCode(e.target.value)}
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-slate-400" />
                      </div>
                      <Input 
                        type="password"
                        placeholder={language === "id" ? "Password Baru" : "New Password"}
                        className="h-14 pl-12 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                      />
                    </div>
                    
                    <Button 
                      onClick={handleResetPassword} 
                      disabled={!forgotCode || !forgotNewPassword || loading}
                      className="w-full h-14 rounded-2xl text-xl font-bold font-display shadow-[0_4px_0_rgba(0,0,0,0.15)] bg-emerald-500 hover:bg-emerald-400"
                    >
                      {loading ? "..." : (language === "id" ? "Ubah Password" : "Reset Password")}
                    </Button>
                  </div>
                )}

                <div className="mt-2 text-center">
                  <button 
                    onClick={() => {
                      setIsForgotMode(false);
                      setHasSentResetCode(false);
                      setForgotCode("");
                      setForgotNewPassword("");
                    }} 
                    className="text-slate-500 font-bold hover:text-sky-600 transition-colors text-sm"
                  >
                    {language === "id" ? "Kembali ke Login" : "Back to Login"}
                  </button>
                </div>
              </motion.div>
            ) : isLoginMode ? (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex flex-col space-y-6"
              >
                <div className="text-center">
                  <h2 className="text-3xl font-display font-bold text-slate-800 mb-2">
                    {language === "id" ? "Masuk ke Akun" : "Login"}
                  </h2>
                  <p className="text-slate-600">
                    {language === "id" ? "Lanjutkan progres dan akses premium Anda." : "Continue your progress and premium access."}
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <Input 
                      type="email"
                      placeholder={language === "id" ? "Alamat Email" : "Email Address"}
                      className="h-14 pl-12 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <Input 
                      type="password"
                      placeholder="••••••••"
                      className="h-14 pl-12 text-lg rounded-2xl bg-white border-2 border-slate-200 focus-visible:ring-sky-500"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <Button 
                    onClick={handleLogin} 
                    disabled={!loginEmail || !loginPassword || loading}
                    className="w-full h-14 rounded-2xl text-xl font-bold font-display shadow-[0_4px_0_rgba(0,0,0,0.15)] bg-sky-500 hover:bg-sky-400"
                  >
                    {loading ? "..." : (language === "id" ? "Masuk" : "Login")}
                  </Button>
                </div>

                <div className="relative mt-2 mb-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#FDFBF7] text-slate-400 font-medium">
                      {language === "id" ? "ATAU" : "OR"}
                    </span>
                  </div>
                </div>

          <div className="flex justify-center w-full min-h-[56px] border-2 border-slate-200 rounded-2xl bg-white overflow-hidden hover:bg-slate-50 transition-colors cursor-pointer relative">
            <div 
              id="google-signin-btn-login" 
              className="w-full h-full flex justify-center items-center py-2"
              onClick={() => {
                if (!window.google?.accounts?.id) {
                  toast.error(
                    language === "id"
                      ? "Modul otentikasi Google belum siap atau diblokir. Silakan masuk dengan Email."
                      : "Google auth module not ready or blocked. Please login with Email."
                  );
                }
              }}
            ></div>
          </div>

                <div className="mt-4 flex flex-col items-center gap-2">
                  <button onClick={() => setIsLoginMode(false)} className="text-slate-500 font-bold hover:text-sky-600 transition-colors">
                    {language === "id" ? "Belum punya akun? Daftar di sini" : "Don't have an account? Register here"}
                  </button>
                  <button 
                    onClick={() => {
                      setIsForgotMode(true);
                      setForgotEmail(loginEmail);
                    }} 
                    className="text-slate-400 text-sm font-bold hover:text-sky-500 transition-colors"
                  >
                    {language === "id" ? "Lupa password?" : "Forgot password?"}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {steps.find((s) => s.id === step)?.content}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
